import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import { ReportType } from "./report-types";
import { ItemByIdPath, ItemPath } from "./get-report";

/**
 * Regeneration re-runs the generator against the report's already-persisted
 * `job.json`, so there is nothing for the caller to supply beyond the report's
 * identity in the URL. POST still requires a body schema (`defineEndpoint`'s
 * POST overload), so we accept — and ignore — an empty object rather than
 * inventing fields the handler would have to reject.
 */
export const RegenerateReportBody = z.object({});
export type RegenerateReportBody = z.output<typeof RegenerateReportBody>;

/**
 * Fire-and-forget acknowledgement. Regeneration is asynchronous: the endpoint
 * enqueues the stored job onto the generator's SQS queue and returns
 * immediately (HTTP 200) with `status: "generating"`. The caller polls the
 * by-id fetch (`{type}/items/by-id/{reportId}`) to observe the flip back to
 * `ready`.
 *
 * (The response is a normal 200 rather than a bare 202 because the shared
 * `route()`/`defineEndpoint` layer serialises a typed body as 200 and validates
 * it against this schema; the `status: "generating"` field carries the
 * "accepted, working on it" meaning, and the client treats any 2xx identically.)
 *
 * `reportId` is echoed so a caller that regenerated by `(type, periodStart)`
 * learns the stable id to poll on. It never changes across a regeneration —
 * the generator's upsert is write-once on the report's identity.
 */
export const RegenerateReportResponse = z.object({
  data: z.object({
    reportId: z.string(),
    type: ReportType,
    periodStart: z.string(),
    status: z.literal("generating"),
  }),
});
export type RegenerateReportResponse = z.output<typeof RegenerateReportResponse>;

/**
 * Regenerate by the stable `reportId` (the canonical identity — the only key
 * unique for cumulative RoadSense reports). Resolves via GSI2, which is NOT
 * tenant-scoped, so the handler owns the tenant-match 403 (mirrors
 * `getReportByIdEndpoint`).
 */
export const regenerateReportByIdResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/id/{reportId}/regenerate",
  resourcePathSchema: ItemByIdPath,
  scope: "tenant",
});

export const regenerateReportByIdEndpoint = defineEndpoint(regenerateReportByIdResource, {
  method: "POST",
  bodySchema: RegenerateReportBody,
  responseSchema: RegenerateReportResponse,
});

/**
 * Regenerate by `(type, periodStart)` — the natural-key surface. Resolves via
 * the main index, which is tenant-partitioned, so a returned row provably
 * belongs to the caller (no cross-tenant guard needed; mirrors
 * `getReportEndpoint`). For a type with one report per period (WasteSense) this
 * is unambiguous; for RoadSense the cumulative report is the sole row sharing
 * that `periodStart`.
 */
export const regenerateReportByPeriodResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/period-start/{periodStart}/regenerate",
  resourcePathSchema: ItemPath,
  scope: "tenant",
});

export const regenerateReportByPeriodEndpoint = defineEndpoint(regenerateReportByPeriodResource, {
  method: "POST",
  bodySchema: RegenerateReportBody,
  responseSchema: RegenerateReportResponse,
});
